import {
    useMemo,
} from "react";


const PAIRS = [
    "BTCUSDT",
    "ETHUSDT",
    "SOLUSDT",
];


const STATUS_STYLES = {
    DEVELOPING: {
        badge:
            "border-amber-400/30 bg-amber-400/10 text-amber-300",

        dot:
            "bg-amber-300",
    },

    READY: {
        badge:
            "border-emerald-400/30 bg-emerald-400/10 text-emerald-300",

        dot:
            "bg-emerald-300",
    },

    "NO SETUP": {
        badge:
            "border-slate-700 bg-slate-900 text-slate-500",

        dot:
            "bg-slate-600",
    },
};


const STEP_DEFINITIONS = [
    [
        "htf_bias",
        "HTF Bias",
    ],

    [
        "liquidity_sweep",
        "Liquidity Sweep",
    ],

    [
        "mss_choch",
        "MSS/CHoCH",
    ],

    [
        "displacement",
        "Displacement",
    ],

    [
        "fvg",
        "FVG",
    ],

    [
        "order_block",
        "Order Block",
    ],

    [
        "breaker_block",
        "Breaker Block",
    ],
];


const DEFAULT_RADAR = (
    pair
) => ({
    pair,

    status:
        "NO SETUP",

    checklist: {
        htf_bias:
            false,

        liquidity_sweep:
            false,

        mss_choch:
            false,

        displacement:
            false,

        fvg:
            false,

        order_block:
            false,

        breaker_block:
            false,
    },

    completed_steps:
        [],

    missing_confluence:
        "Waiting for market structure to develop",

    distance_to_trigger:
        null,

    trigger_level:
        null,

    trigger_source:
        null,

    current_price:
        null,

    updated_at:
        null,
});


const normalizeStatus = (
    value
) => {
    const status =
        String(
            value ??
                "NO SETUP"
        )
            .trim()
            .toUpperCase();

    return Object.prototype.hasOwnProperty.call(
        STATUS_STYLES,
        status
    )
        ? status
        : "DEVELOPING";
};


const normalizeRadar = (
    pair,
    raw
) => {
    const source =
        raw?.payload &&
        typeof raw.payload ===
            "object"
            ? raw.payload
            : raw ?? {};

    return {
        ...DEFAULT_RADAR(
            pair
        ),

        ...source,

        pair:
            String(
                source.pair ??
                    source.symbol ??
                    pair
            )
                .trim()
                .toUpperCase(),

        status:
            normalizeStatus(
                source.status
            ),

        checklist: {
            ...DEFAULT_RADAR(
                pair
            ).checklist,

            ...(source.checklist ??
                {}),
        },
    };
};


const formatPrice = (
    value
) => {
    const number =
        Number(value);

    if (
        !Number.isFinite(
            number
        )
    ) {
        return "—";
    }

    return number.toLocaleString(
        undefined,
        {
            maximumFractionDigits:
                Math.abs(number) >=
                1000
                    ? 2
                    : 6,
        }
    );
};


const formatDistance = (
    value
) => {
    const number =
        Number(value);

    if (
        !Number.isFinite(
            number
        )
    ) {
        return "—";
    }

    return formatPrice(
        number
    );
};


export function MarketRadar({
    radarByPair =
        {},
}) {
    const cards =
        useMemo(
            () =>
                PAIRS.map(
                    (
                        pair
                    ) =>
                        normalizeRadar(
                            pair,
                            radarByPair?.[
                                pair
                            ]
                        )
                ),

            [
                radarByPair,
            ]
        );

    return (
        <section className="rounded-2xl border border-slate-800 bg-slate-900/80 p-5 shadow-2xl shadow-black/10">

            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">

                <div>

                    <div className="flex items-center gap-2">

                        <span className="h-2 w-2 animate-pulse rounded-full bg-cyan-300" />

                        <h2 className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-200">
                            Live Market Radar
                        </h2>

                    </div>

                    <p className="mt-1 text-xs text-slate-500">
                        Continuous deterministic SMC confluence state across BTC, ETH and SOL.
                    </p>

                </div>

                <span className="text-[11px] uppercase tracking-wider text-slate-600">
                    Read-only · execution authority remains deterministic
                </span>

            </div>


            <div className="grid gap-4 lg:grid-cols-3">

                {cards.map(
                    (
                        card
                    ) => {
                        const style =
                            STATUS_STYLES[
                                card.status
                            ] ??
                            STATUS_STYLES[
                                "NO SETUP"
                            ];

                        return (
                            <article
                                key={
                                    card.pair
                                }
                                className="rounded-xl border border-slate-800 bg-slate-950/70 p-4"
                            >

                                <div className="flex items-start justify-between gap-3">

                                    <div>

                                        <h3 className="text-base font-bold text-white">
                                            {
                                                card.pair
                                            }
                                        </h3>

                                        <p className="mt-1 text-[11px] text-slate-500">
                                            Live setup progression
                                        </p>

                                    </div>


                                    <span
                                        className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider ${style.badge}`}
                                    >

                                        <span
                                            className={`h-1.5 w-1.5 rounded-full ${style.dot}`}
                                        />

                                        {
                                            card.status
                                        }

                                    </span>

                                </div>


                                <div className="mt-4 space-y-2">

                                    {STEP_DEFINITIONS.map(
                                        ([
                                            key,
                                            label,
                                        ]) => {
                                            const complete =
                                                card
                                                    .checklist[
                                                    key
                                                ] ===
                                                true;

                                            return (
                                                <div
                                                    key={
                                                        key
                                                    }
                                                    className="flex items-center gap-2.5 text-xs"
                                                >

                                                    <span
                                                        className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border text-[11px] font-bold ${
                                                            complete
                                                                ? "border-emerald-400/40 bg-emerald-400/10 text-emerald-300"
                                                                : "border-slate-700 bg-slate-900 text-slate-600"
                                                        }`}
                                                    >
                                                        {complete
                                                            ? "✓"
                                                            : ""}
                                                    </span>

                                                    <span
                                                        className={
                                                            complete
                                                                ? "text-slate-200"
                                                                : "text-slate-600"
                                                        }
                                                    >
                                                        {
                                                            label
                                                        }
                                                    </span>

                                                </div>
                                            );
                                        }
                                    )}

                                </div>


                                <div className="mt-4 rounded-lg border border-slate-800 bg-slate-900/70 p-3">

                                    <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-600">
                                        Missing confluence
                                    </p>

                                    <p className="mt-1 text-xs leading-5 text-slate-300">
                                        {
                                            card.missing_confluence ??
                                            "Waiting for market structure to develop"
                                        }
                                    </p>

                                </div>


                                <div className="mt-3 grid grid-cols-2 gap-3 border-t border-slate-800 pt-3 text-[11px]">

                                    <div>

                                        <span className="block text-slate-600">
                                            Distance to trigger
                                        </span>

                                        <span className="mt-1 block font-mono text-slate-300">
                                            {
                                                formatDistance(
                                                    card.distance_to_trigger
                                                )
                                            }
                                        </span>

                                    </div>


                                    <div className="text-right">

                                        <span className="block text-slate-600">
                                            Key level
                                        </span>

                                        <span className="mt-1 block font-mono text-slate-300">
                                            {
                                                formatPrice(
                                                    card.trigger_level
                                                )
                                            }
                                        </span>

                                    </div>

                                </div>


                                <div className="mt-2 flex justify-between text-[10px] text-slate-700">

                                    <span>
                                        {
                                            card.current_price !==
                                            null
                                                ? `Price ${formatPrice(
                                                      card.current_price
                                                  )}`
                                                : "Price —"
                                        }
                                    </span>

                                    <span>
                                        {
                                            card.updated_at
                                                ? new Date(
                                                      card.updated_at
                                                  ).toLocaleTimeString()
                                                : "Waiting"
                                        }
                                    </span>

                                </div>

                            </article>
                        );
                    }
                )}

            </div>

        </section>
    );
}


export default MarketRadar;